/**
 * /engine — "Under the hood" technical deep-dive page.
 *
 * Shell mirrors /attorneys + /blog: PublicHeader + `.engine-page` main +
 * shared Footer. 8 sections alternating bg / soft / dark for visual
 * rhythm (section 03 Multi-Agent is intentionally dark — don't collapse
 * the alternation).
 *
 * IL Y EN AVAIT 10. « 06 · Benchmarks » et « 07 · Précision mesurée » ont
 * été supprimées le 17/08/2026 : elles annonçaient des mesures qui
 * n'existent pas (cf. le commentaire au-dessus du rendu). Leur retrait
 * laissait LegalRAG et TechnicalInnovations tous deux en fond clair, donc
 * la fin de page a été décalée d'un cran — c'est ce que demande la
 * consigne d'alternation ci-dessus.
 *
 * Jurisdiction flows down to SecurityPrivacy so the hero title + two
 * specific cards (Regional servers, Privacy-law compliant) swap between
 * Belgium/GDPR, the United States/CCPA, and a neutral fallback.
 */
import React from 'react';
import PageHead from '../components/seo/PageHead';
import { getPageMetadata } from '../lib/seo/metadata';
import PublicHeader from '../components/PublicHeader';
import SiteFooterV2 from '../components/siteV2/SiteFooterV2';
import { useEngineT } from '../hooks/useEngineT';
import { useSiteLanguage, readSiteCountry } from '../hooks/useSiteLanguage';

import EngineHero from '../components/engine/EngineHero';
import PipelineCards from '../components/engine/PipelineCards';
import KnowledgeBase from '../components/engine/KnowledgeBase';
import MultiAgent from '../components/engine/MultiAgent';
import ModelOrchestration from '../components/engine/ModelOrchestration';
import LegalRAG from '../components/engine/LegalRAG';
import TechnicalInnovations from '../components/engine/TechnicalInnovations';
import RadicalTransparency from '../components/engine/RadicalTransparency';
import SecurityPrivacy from '../components/engine/SecurityPrivacy';

import '../styles/home.css';
import '../styles/engine.css';
import '../styles/hero.css';

// LANGUE — cette page tenait sa propre copie de la langue : un `useState`
// local alimenté par le callback `onLanguageChange` du header, avec son
// propre repli ('be-fr') et son propre `useEffect` de montage. Trois
// mécanismes pour dire une chose que `useSiteLanguage` dit déjà, et une
// deuxième source de vérité à côté de celle du header et du footer.
//
// Elle lit désormais le même store que tout le chrome public. Le repli
// belge francophone n'a pas disparu : il est dans `useSiteLanguage`
// (DEFAULT_SITE_LANGUAGE), pour le site entier et non plus pour cette
// seule page.

export default function EnginePage() {
  const [language] = useSiteLanguage();
  // Même clé, même store : la juridiction se relit à chaque rendu déclenché
  // par le changement de langue. Le sélecteur n'écrit que `be-<lang>` — la
  // Belgique est la seule juridiction active — donc les deux ne peuvent
  // pas diverger.
  const country = readSiteCountry();

  const t = useEngineT(language);

  // Les balises viennent de la table SEO commune, comme celles de toutes
  // les autres pages du menu. Cette page les fabriquait à la volée depuis
  // `engine.json` : elle y gagnait un titre, mais y perdait le hreflang —
  // elle sert pourtant ses trois langues à la même URL. Le repli couvre
  // une langue inattendue sans jamais rendre une page sans balises.
  const metadata =
    getPageMetadata('engine', language) || getPageMetadata('engine', 'fr');

  return (
    <div data-testid="engine-page">
      <PageHead metadata={metadata} />
      <PublicHeader />

      <main className="engine-page">
        <EngineHero t={t} language={language} />
        <PipelineCards t={t} language={language} />
        <KnowledgeBase t={t} language={language} />
        <MultiAgent t={t} />
        <ModelOrchestration t={t} />
        <LegalRAG t={t} language={language} />
        {/* « 06 · Benchmarks » et « 07 · Précision mesurée » ont été
            SUPPRIMÉES le 17/08/2026, composants et clés i18n compris.

            La 07 affichait six métriques — 94,7 % de précision, 89,3 % de
            taux de succès, 96 % de lettres acceptées, 8,4/10, NPS 72 —
            sous le sous-titre « Chaque métrique est mesurée sur des
            dossiers en production, revue chaque semaine, et publiquement
            vérifiable ». Aucune n'était mesurée : le tableau était un
            littéral, sans appel réseau ni source.

            La 06 comparait Archer aux alternatives sous « 412 dossiers
            juridiques anonymisés, plusieurs domaines, évaluation en
            double aveugle par 3 avocats partenaires ». Cette étude
            n'existe nulle part dans le dépôt, et la ligne « prix »
            affichait 49,99 €, un montant absent de la grille.

            Une méthodologie chiffrée et nommée appelle la demande du
            protocole. Ne pas les réécrire : ce qui est réellement
            mesurable est déjà à l'écran — le corpus (300 000+, verrouillé
            par `tests/test_corpus_count.py`) et la dimension des
            embeddings. */}
        <TechnicalInnovations t={t} />
        <RadicalTransparency t={t} />
        <SecurityPrivacy t={t} country={country} />
      </main>

      <SiteFooterV2 />
    </div>
  );
}
